/* eslint-disable array-callback-return */
import React, {useEffect, useMemo, useState} from 'react'
import {AktivnePaleteModel, ParamsModelAktivnePalete} from '../../models/AktivnePaleteModel'
import {PaginationModel} from '../../models/PaginationModel'
import {
    SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'
import {getAktivniPalete} from '../../api/aktivnePalete'
import notifyToast from '../../components/Toast/Toast'
import {ToastContainer} from 'react-toastify'
import {KTCard, KTCardBody, KTIcon} from '../../../_websoft/helpers'
import ArtikliDropdown from '../../components/ArtikliDropdown/ArtikliDropdown'
import TableSearch from '../../components/TableSearch/TableSearch'
import ExportButton from '../../components/TableExportToExcel/ExportButton'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import TablePagination from '../../components/TablePagination/TablePagination'
import {Modal} from 'react-bootstrap'
import AktivnePaletaQrCode from '../../components/QrCodeGenerator/AktivnePaletaQrCode'

const AktivnePalete = () => {
    const [data, setData] = useState<AktivnePaleteModel[]>([])
    const [loading, setLoading] = useState(true)

    const [pagination, setpagination] = useState<PaginationModel>({
        curPage: 1,
        totalPage: 1,
        perPage: 10,
    })

    // NOTE: you had setparams but never actually update state due to destructuring.
    // Keeping same logic but making it real state so updates trigger fetch.
    const [params, setparams] = useState<ParamsModelAktivnePalete>({per_page: 10, page: 1})

    const [sorting, setSorting] = useState<SortingState>([])
    const [globalFilter, setGlobalFilter] = useState('')

    const [suma, setSuma] = useState(0)
    const [isSumaOpen, setisSumaOpen] = useState(false)

    // ✅ PDF / QR (lazy)
    const [pdfOpen, setPdfOpen] = useState(false)
    const [pdfRow, setPdfRow] = useState<AktivnePaleteModel | null>(null)

    const openPdfModal = (row: AktivnePaleteModel) => {
        setPdfRow(row)
        setPdfOpen(true)
    }

    const closePdfModal = () => {
        setPdfOpen(false)
        // keep row to avoid flicker; or clear it:
        // setPdfRow(null)
    }

    // OPTIONAL: If you have a dedicated backend endpoint or frontend route for PDF,
    // set it here. Example:
    // const buildPdfUrl = (row: AktivnePaleteModel) => `/aktivne-palete/pdf/${row.ArtPaletaId}`
    const buildPdfUrl = (row: AktivnePaleteModel) => `/aktivne-palete/pdf/${(row as any).ArtPaletaId}`

    const updatePage = () => {
        setLoading(true)
        getAktivniPalete(params)
            .then((response) => {
                const rows = response.data['data']
                setData(rows)

                const totalPage = Math.floor(response.data['total'] / response.data['per_page'] + 1)
                setpagination({
                    curPage: response.data['current_page'],
                    totalPage: totalPage,
                    perPage: params.per_page,
                })
            })
            .catch((error) => {
                console.error('Error: ', error)
                notifyToast(false, error)
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        updatePage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params])

    // ✅ Avoid mutating data with a side-effect sort (this can cause weird rerenders)
    // TanStack already sorts via sorting state.
    // So we REMOVE:
    // useEffect(() => { data.sort(...) }, [sorting])

    // Sum of ArtPaletaTrenutnoStanje.
    const handleSuma = (table: any) => {
        let sum = 0
        table.getRowModel().rows.forEach((row: any) =>
            row.getVisibleCells().forEach((cell: any) => {
                if (cell.column.id === 'Trstanje') {
                    sum += parseFloat((cell.getValue() ?? '0') as any)
                }
            })
        )
        setSuma(sum)
    }

    // Set cell color according to conditions.
    const getCellColor = (cell: any) => {
        if (cell.column.id === 'Raspolozivost') {
            return cell.getValue() === 'Raspoloživ' ? '#008000' : '#FF0000'
        }
        return '#FFFFFF'
    }

    const formatNumber = (number: number, locales = 'bs-BA', maximumFractionDigits = 2) => {
        return new Intl.NumberFormat(locales, {
            maximumFractionDigits,
            minimumFractionDigits: maximumFractionDigits,
        }).format(number)
    }

    // ✅ Columns memoized (important for performance)
    const columns = useMemo(
        () => [
            {id: 'Sifra', header: 'Sifra', accessorKey: 'artikal.ArtSifra'},
            {id: 'Artikal', header: 'Artikal', accessorKey: 'artikal.ArtNaziv'},
            {id: 'Paletabr', header: 'Paleta br', accessorKey: 'ArtPaletaId'},
            {id: 'Ulaz', header: 'Ulaz', accessorKey: 'ArtPaletaUlaz'},
            {id: 'Trstanje', header: 'Tr.stanje', accessorKey: 'ArtPaletaTrenutnoStanje'},
            {id: 'Jedinicamjere', header: 'Jedinica mjere', accessorKey: 'jedinica_mjere.JmOznaka'},
            {id: 'Kodboje', header: 'Kodboje', accessorKey: 'kodboje.KodBojeNaziv'},
            {id: 'Dokument', header: 'Dokument', accessorKey: 'dokument.DokNaziv'},
            {id: 'Lokacija', header: 'Lokacija', accessorKey: 'ArtPaletaLokacija'},

            // ✅ LIGHT column: no PDF rendering in table
            {
                id: 'Pdf',
                header: 'Pdf',
                cell: ({row}: any) => (
                    <button
                        type='button'
                        className='btn btn-sm btn-light-primary d-inline-flex align-items-center gap-2'
                        onClick={() => openPdfModal(row.original)}
                        title='Prikaži PDF'
                    >
                        <KTIcon iconName='document' className='fs-4' />
                        <span className='d-none d-md-inline'>PDF</span>
                    </button>
                ),
            },

            {id: 'Raspolozivost', header: 'Raspoloživost', accessorKey: 'ArtPaletaRaspolozivost'},
        ],
        []
    )

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,
        getFilteredRowModel: getFilteredRowModel(),
        onSortingChange: setSorting,
    })

    return (
        <KTCard>
            <ToastContainer />

            {/* SUMA MODAL */}
            <Modal show={isSumaOpen} onHide={() => setisSumaOpen(false)} centered>
                <Modal.Header closeButton className='py-4'>
                    <Modal.Title className='fw-bold'>Suma</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='fs-2 fw-bolder'>{formatNumber(suma)}</div>
                </Modal.Body>
            </Modal>

            {/* PDF MODAL (lazy render heavy component only here) */}
            <Modal show={pdfOpen} onHide={closePdfModal} size='lg' centered>
                <Modal.Header closeButton className='py-4'>
                    <Modal.Title className='fw-bold d-flex align-items-center gap-2'>
                        <KTIcon iconName='document' className='fs-2' />
                        PDF / QR Code
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {!pdfRow ? (
                        <div className='text-muted'>Nema podataka.</div>
                    ) : (
                        <>
                            <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4'>
                                <div className='text-muted'>
                                    Paleta: <span className='fw-bold fs-2'>{(pdfRow as any).ArtPaletaId}</span>
                                </div>
                            </div>

                            {/* ✅ Heavy component renders only for selected row */}
                            <AktivnePaletaQrCode data={pdfRow} />
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button type='button' className='btn btn-light' onClick={closePdfModal}>
                        Zatvori
                    </button>
                </Modal.Footer>
            </Modal>

            <KTCardBody className='py-4'>
                <div className='col-12 col-md-6 col-lg-3 mb-6'>
                    <h3 className='fw-bolder mt-3 mb-3'>Odaberi artikal</h3>
                    <ArtikliDropdown onChange={(name, value) => setparams({...params, artikal: value})} />
                </div>

                <div className='d-flex justify-content-between flex-wrap gap-3'>
                    <div data-kt-user-table-toolbar='base' className='flex-grow-1'>
                        <TableSearch onSearch={(text) => setGlobalFilter(text)} />
                    </div>

                    <div data-kt-user-table-toolbar='base' className='d-flex gap-2'>
                        <button
                            className='btn btn-light-primary'
                            onClick={() => {
                                handleSuma(table)
                                setisSumaOpen(true)
                            }}
                        >
                            <KTIcon iconName='calculator' className='fs-2 me-2' />
                            Suma
                        </button>

                        <ExportButton table={table} filename={'aktivne-palete'} />
                    </div>
                </div>

                {loading ? (
                    <LoadingComponent />
                ) : (
                    <div className='table-responsive mt-3'>
                        <table className='table align-middle table-bordered gy-5 no-footer'>
                            <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id} className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            className='text-white'
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{cursor: 'pointer'}}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {/* @ts-ignore */}
                                            {{asc: ' 🔼', desc: ' 🔽'}[header.column.getIsSorted() ?? null]}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>

                            <tbody className='text-gray-600'>
                            {table.getRowModel().rows && table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} style={{backgroundColor: getCellColor(cell)}}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={12}>
                                        <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                                            No matching records found
                                        </div>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                )}

                <TablePagination
                    updatePage={(page, per_page) => setparams({...params, page: page, per_page: per_page})}
                    pagination={pagination}
                />
            </KTCardBody>
        </KTCard>
    )
}

export default AktivnePalete
